import { useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import {
  EmbedVideoCard,
  RecordingVideoCard,
} from "@/components/editor/shared/media/video-cards";
import {
  fetchRecordingsById,
  getRecordingsById,
} from "@/features/recordings/recordings-reducer";
import useEvent from "@/hooks/use-event";
import { useAppDispatch } from "@/redux/store";

import { VideoSettings } from "./settings";
import { VideoContainer } from "./video-component-styles";

type VideoContent = {
  url?: string;
  thumbnail?: string;
  title?: string;
  recordingId?: string;
};

export type VideoBlockProps = {
  isMuted?: boolean;
} & VideoContent;

export default function Video(props: VideoBlockProps) {
  const dispatch = useAppDispatch();
  const { event } = useEvent();
  const { preEventOrganizationSpaces: spacesEnabled } = useFlags();
  const {
    connectors: { connect },
  } = useNode();

  const recording = useSelector(getRecordingsById(props.recordingId));

  const spaceExternalId = spacesEnabled
    ? event.space_external_id ?? undefined
    : undefined;

  useEffect(() => {
    if (!props.recordingId) {
      return;
    }

    const promise = dispatch(
      fetchRecordingsById({
        organizationExternalId: event.organization.external_id,
        recordingIds: [props.recordingId],
        spaceExternalId,
      })
    );
    return () => {
      promise.abort();
    };
  }, [
    dispatch,
    event.organization.external_id,
    props.recordingId,
    spaceExternalId,
  ]);

  return (
    <VideoContainer ref={connect} data-testid="video-component">
      {props.recordingId && recording ? (
        <RecordingVideoCard
          key={recording.id}
          showCaption={true}
          recording={recording}
          aspectRatio={"wide"}
          playerOptions={{
            muted: props.isMuted,
          }}
        />
      ) : (
        <EmbedVideoCard
          url={props.url}
          showCaption={true}
          title={props.title}
          thumbnail={props.thumbnail}
          playerOptions={{
            muted: props.isMuted,
          }}
          aspectRatio="wide"
        />
      )}
    </VideoContainer>
  );
}

export const VIDEO_PRIMITIVE_DISPLAY_NAME = "Video";

Video.craft = {
  related: {
    settings: VideoSettings,
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
  custom: {
    displayName: VIDEO_PRIMITIVE_DISPLAY_NAME,
  },
};
